"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReceiptDocumentIR = exports.ReceiptElementSpec = exports.ReceiptElement = exports.ReceiptLegalTextElement = exports.ReceiptFooterElement = exports.ReceiptFiscalBlockElement = exports.ReceiptTotalsBlockElement = exports.ReceiptVatBreakdownEntry = exports.ReceiptItemLineElement = exports.ReceiptHeaderElement = void 0;
const zod_1 = require("zod");
const receipt_config_js_1 = require("./receipt-config.js");
/**
 * Receipt rendering pipeline IR (TRS §35.5). The intermediate
 * representation produced by the BOS-side renderer (Sprint 3 ticket
 * 3.7) and consumed by the terminal-side target renderers (ESC/POS
 * for thermal printers, Cat-A device format, PDF, HTML, JSON for
 * third-party integrations — Flutter side, cross-repo).
 *
 * Per TRS §35.5: "each element has its resolved strings (no
 * unresolved keys); no target-device concerns here (no thermal
 * widths, no font sizes)." This file mirrors that contract — every
 * string is pre-resolved to the terminal's locale + currency
 * formatting, and there are no rendering hints.
 *
 * Sprint 3 baseline element types: header, item_line, totals_block,
 * fiscal_block, footer, legal_text. Sprint 7 / Sprint 11 production
 * packs may extend with barcode, qr_code, separator, etc. — add new
 * variants to the discriminated union when needed.
 */
exports.ReceiptHeaderElement = zod_1.z.object({
    type: zod_1.z.literal('header'),
    retailerName: zod_1.z.string().optional(),
    logoRef: zod_1.z.string().optional(),
    /** Pre-rendered metadata lines (e.g. "Vilnius store / Counter 3"). */
    lines: zod_1.z.array(zod_1.z.string()),
});
exports.ReceiptItemLineElement = zod_1.z.object({
    type: zod_1.z.literal('item_line'),
    /** Pre-resolved item label in the terminal's locale. */
    name: zod_1.z.string(),
    /** Pre-formatted quantity ("1", "2,000 kg"). */
    quantityFormatted: zod_1.z.string(),
    /** Pre-formatted unit price ("€1,99"). */
    unitPriceFormatted: zod_1.z.string().optional(),
    /** Pre-formatted line total ("€3,98"). */
    lineTotalFormatted: zod_1.z.string(),
    /** Optional SKU field; renderer hides if `show_sku` config is false. */
    sku: zod_1.z.string().optional(),
    /** Optional VAT rate display per line; renderer hides if `show_vat_per_line` is false. */
    vatRateFormatted: zod_1.z.string().optional(),
    /** Pre-formatted line discount, if any. */
    discountFormatted: zod_1.z.string().optional(),
});
exports.ReceiptVatBreakdownEntry = zod_1.z.object({
    rateFormatted: zod_1.z.string(),
    baseFormatted: zod_1.z.string(),
    vatFormatted: zod_1.z.string(),
});
exports.ReceiptTotalsBlockElement = zod_1.z.object({
    type: zod_1.z.literal('totals_block'),
    subtotalFormatted: zod_1.z.string(),
    /** Optional aggregate or per-line discount, controlled by `discounts_presentation`. */
    discountFormatted: zod_1.z.string().optional(),
    /** Optional per-rate VAT breakdown — populated when retailer config requests it. */
    vatBreakdown: zod_1.z.array(exports.ReceiptVatBreakdownEntry).optional(),
    totalFormatted: zod_1.z.string(),
});
exports.ReceiptFiscalBlockElement = zod_1.z.object({
    type: zod_1.z.literal('fiscal_block'),
    /** Receipt number assigned by the fiscal device / software registrar. */
    receiptNumber: zod_1.z.string(),
    /** Fiscal id / signature reference produced by the FiscalAdapter (TRS §9.2). */
    fiscalNumber: zod_1.z.string(),
    /** Pre-formatted fiscal timestamp in the country pack's mandated format. */
    timestampFormatted: zod_1.z.string(),
    /** Optional cryptographic signature payload (Cat B / PRRO). */
    signature: zod_1.z.string().optional(),
    /**
     * Optional QR-code payload (string the terminal renderer encodes).
     * Sprint 7 LT production produces VMI-required QR; Sprint 11 UA
     * produces PRRO-required QR. Sprint 3 stub leaves null.
     */
    qrPayload: zod_1.z.string().optional(),
});
exports.ReceiptFooterElement = zod_1.z.object({
    type: zod_1.z.literal('footer'),
    retailerFooterText: zod_1.z.string().optional(),
    lines: zod_1.z.array(zod_1.z.string()),
});
exports.ReceiptLegalTextElement = zod_1.z.object({
    type: zod_1.z.literal('legal_text'),
    /** Verbatim mandatory text from the country pack. NEVER user-translatable. */
    text: zod_1.z.string().min(1),
    /** ISO-639 lowercase code matching the pack's `mandatoryFiscalText.language`. */
    language: zod_1.z.string().regex(/^[a-z]{2,3}$/),
    /**
     * The pack-side block id this text was pulled from (for traceability /
     * Sprint 12 reconciliation).
     */
    sourceBlockId: zod_1.z.string().optional(),
});
exports.ReceiptElement = zod_1.z.discriminatedUnion('type', [
    exports.ReceiptHeaderElement,
    exports.ReceiptItemLineElement,
    exports.ReceiptTotalsBlockElement,
    exports.ReceiptFiscalBlockElement,
    exports.ReceiptFooterElement,
    exports.ReceiptLegalTextElement,
]);
exports.ReceiptElementSpec = exports.ReceiptElement; // alias the dev-plan refers to
exports.ReceiptDocumentIR = zod_1.z.object({
    receiptType: receipt_config_js_1.ReceiptType,
    countryPackId: zod_1.z.string(),
    packVersion: zod_1.z.string(),
    /**
     * Locale used to resolve user-facing strings (NOT the
     * mandatory-fiscal-text language). H-58: deliberately
     * ISO-639-1/2/3 only — no BCP-47 region qualifier (`lt-LT`).
     * Matches the i18n service's internal codes. If a Phase-2
     * customer needs region-aware rendering, this widens to BCP-47
     * platform-wide; for now keep the surface narrow so the IR
     * stays predictable.
     */
    locale: zod_1.z.string().regex(/^[a-z]{2,3}$/),
    elements: zod_1.z.array(exports.ReceiptElement).min(1),
    generatedAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=receipt-document-ir.js.map